import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  listContracts,
  getContract,
  createContract,
  sendContractForSigning,
  signContract,
  cancelContract,
  getMilestones,
  fundMilestoneEscrow,
  reviewSubmission,
  approveMilestonePayment,
  createDispute,
  getDisputes,
  respondToDispute,
  sendMessage,
  getMessages,
  markMessagesRead,
} from '../../services/contracts';
import type {
  Contract,
  ContractSummary,
  Milestone,
  MilestoneSubmission,
  Dispute,
  ContractMessage,
  CreateContractRequest,
  CreateMilestoneRequest,
  ReviewSubmissionRequest,
  SubmissionStatus,
  DisputeType,
} from '../../types/contract';
import {
  getContractStatusLabel,
  getContractStatusColor,
  getMilestoneStatusLabel,
  getMilestoneStatusColor,
  getDisputeStatusLabel,
  formatContractAmount,
  formatDate,
  formatDateTime,
  getContractProgress,
} from '../../types/contract';

export default function ContractsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // If we have an ID, show the detail view
  if (id) {
    return <ContractDetailView contractId={id} onBack={() => navigate('/company/contracts')} />;
  }

  // Otherwise show the list view
  return <ContractListView onSelectContract={(id) => navigate(`/company/contracts/${id}`)} />;
}

// ============================================================================
// Contract List View
// ============================================================================

interface ContractListViewProps {
  onSelectContract: (id: string) => void;
}

function ContractListView({ onSelectContract }: ContractListViewProps) {
  const [contracts, setContracts] = useState<ContractSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'draft' | 'pending' | 'active' | 'completed'>('active');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadContracts();
  }, [activeTab]);

  async function loadContracts() {
    try {
      setLoading(true);
      setError(null);
      const statusFilter = activeTab === 'all' ? undefined : activeTab;
      const response = await listContracts({ status: statusFilter });
      setContracts(response.contracts);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateContract(data: CreateContractRequest) {
    try {
      const contract = await createContract(data);
      setShowCreateModal(false);
      onSelectContract(contract.id);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to create contract');
    }
  }

  const filteredContracts = contracts;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Contracts</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Create Contract
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          {(['active', 'draft', 'pending', 'completed', 'all'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredContracts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No contracts found</p>
          <p className="text-sm mt-2">Create a contract to get started</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredContracts.map((contract) => (
            <ContractCard
              key={contract.id}
              contract={contract}
              onClick={() => onSelectContract(contract.id)}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateContractModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateContract}
        />
      )}
    </div>
  );
}

interface ContractCardProps {
  contract: ContractSummary;
  onClick: () => void;
}

function ContractCard({ contract, onClick }: ContractCardProps) {
  const progress = getContractProgress(contract);
  const statusColor = getContractStatusColor(contract.status);
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold">{contract.title}</h3>
          <p className="text-sm text-gray-500">
            {formatDate(contract.start_date)} - {formatDate(contract.end_date)}
          </p>
        </div>
        <span className={`px-3 py-1 text-sm font-medium rounded-full ${colorClasses[statusColor] || colorClasses.gray}`}>
          {getContractStatusLabel(contract.status)}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-500">Total Value</p>
          <p className="font-semibold">{formatContractAmount(contract.total_amount_cents, contract.currency)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Paid</p>
          <p className="font-semibold">{formatContractAmount(contract.paid_amount_cents, contract.currency)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Milestones</p>
          <p className="font-semibold">{contract.paid_milestones}/{contract.total_milestones}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Pending Review</p>
          <p className="font-semibold">{contract.submitted_milestones}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-green-600 h-2 rounded-full transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Badges */}
      <div className="flex gap-2 mt-4">
        {contract.submitted_milestones > 0 && (
          <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
            {contract.submitted_milestones} awaiting review
          </span>
        )}
        {contract.open_disputes > 0 && (
          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
            {contract.open_disputes} open dispute{contract.open_disputes > 1 ? 's' : ''}
          </span>
        )}
        {contract.unread_messages > 0 && (
          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
            {contract.unread_messages} unread message{contract.unread_messages > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Contract Detail View
// ============================================================================

interface ContractDetailViewProps {
  contractId: string;
  onBack: () => void;
}

function ContractDetailView({ contractId, onBack }: ContractDetailViewProps) {
  const [contract, setContract] = useState<Contract | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [messages, setMessages] = useState<ContractMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  // Modal states
  const [showReviewModal, setShowReviewModal] = useState<{ milestone: Milestone; submission: MilestoneSubmission } | null>(null);
  const [showFundModal, setShowFundModal] = useState<Milestone | null>(null);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Message input
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    loadContractData();
  }, [contractId]);

  async function loadContractData() {
    try {
      setLoading(true);
      setError(null);

      const [contractData, milestonesData, disputesData, messagesData] = await Promise.all([
        getContract(contractId),
        getMilestones(contractId),
        getDisputes(contractId),
        getMessages(contractId),
      ]);

      setContract(contractData);
      setMilestones(milestonesData);
      setDisputes(disputesData);
      setMessages(messagesData.messages);

      // Mark messages as read
      await markMessagesRead(contractId);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  }

  async function handleSendForSigning() {
    if (!confirm('Send this contract to the expert for review and signing?')) return;

    try {
      setProcessing(true);
      const updated = await sendContractForSigning(contractId);
      setContract(updated);
      setSuccess('Contract sent for signing');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to send contract');
    } finally {
      setProcessing(false);
    }
  }

  async function handleSignContract() {
    if (!confirm('By signing, you agree to the terms of this contract. Continue?')) return;

    try {
      setProcessing(true);
      const updated = await signContract(contractId, { acknowledged: true });
      setContract(updated);
      setSuccess('Contract signed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to sign contract');
    } finally {
      setProcessing(false);
    }
  }

  async function handleCancelContract(reason: string) {
    try {
      setProcessing(true);
      await cancelContract(contractId, reason);
      setContract({ ...contract!, status: 'cancelled', cancellation_reason: reason });
      setShowCancelModal(false);
      setSuccess('Contract cancelled');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to cancel contract');
    } finally {
      setProcessing(false);
    }
  }

  async function handleFundMilestone(milestoneId: string, paymentMethodId: string) {
    try {
      setProcessing(true);
      const updated = await fundMilestoneEscrow(contractId, milestoneId, { payment_method_id: paymentMethodId });
      setMilestones(milestones.map((m) => (m.id === milestoneId ? updated : m)));
      setShowFundModal(null);
      setSuccess('Milestone funded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to fund milestone');
    } finally {
      setProcessing(false);
    }
  }

  async function handleReviewSubmission(
    milestoneId: string,
    submissionId: string,
    data: ReviewSubmissionRequest
  ) {
    try {
      setProcessing(true);
      await reviewSubmission(contractId, milestoneId, submissionId, data);

      // Reload milestones to get updated status
      const updatedMilestones = await getMilestones(contractId);
      setMilestones(updatedMilestones);
      setShowReviewModal(null);

      const action = data.decision === 'approved' ? 'approved' : 'sent for revision';
      setSuccess(`Submission ${action}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to review submission');
    } finally {
      setProcessing(false);
    }
  }

  async function handleApprovePayment(milestoneId: string) {
    if (!confirm('Release payment for this milestone? This action cannot be undone.')) return;

    try {
      setProcessing(true);
      const updated = await approveMilestonePayment(contractId, milestoneId);
      setMilestones(milestones.map((m) => (m.id === milestoneId ? updated : m)));
      setSuccess('Payment released to expert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to release payment');
    } finally {
      setProcessing(false);
    }
  }

  async function handleCreateDispute(data: { milestone_id?: string; dispute_type: DisputeType; title: string; description: string }) {
    try {
      setProcessing(true);
      const dispute = await createDispute(contractId, data);
      setDisputes([...disputes, dispute]);
      setShowDisputeModal(false);
      setSuccess('Dispute created');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to create dispute');
    } finally {
      setProcessing(false);
    }
  }

  async function handleSendMessage() {
    if (!newMessage.trim()) return;

    try {
      const message = await sendMessage(contractId, { message: newMessage });
      setMessages([...messages, message]);
      setNewMessage('');
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to send message');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <button onClick={onBack} className="text-blue-600 hover:text-blue-700 mb-4">
          ← Back to contracts
        </button>
        <div className="text-center py-12 text-gray-500">Contract not found</div>
      </div>
    );
  }

  const statusColor = getContractStatusColor(contract.status);
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
  };

  const canSendForSigning = contract.status === 'draft';
  const canSign = contract.status === 'pending' && !contract.company_signed_at;
  const canCancel = ['draft', 'pending'].includes(contract.status);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <button onClick={onBack} className="text-blue-600 hover:text-blue-700 mb-4">
        ← Back to contracts
      </button>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg">{success}</div>
      )}

      {/* Contract Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold mb-2">{contract.title}</h1>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${colorClasses[statusColor] || colorClasses.gray}`}>
              {getContractStatusLabel(contract.status)}
            </span>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-green-600">
              {formatContractAmount(contract.total_amount_cents, contract.currency)}
            </p>
            <p className="text-sm text-gray-500">Total Value</p>
          </div>
        </div>

        {contract.description && (
          <p className="text-gray-600 mb-4">{contract.description}</p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-500">Start Date</p>
            <p className="font-medium">{formatDate(contract.start_date)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">End Date</p>
            <p className="font-medium">{formatDate(contract.end_date)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Platform Fee</p>
            <p className="font-medium">{(contract.platform_fee_rate * 100).toFixed(0)}%</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Expert ID</p>
            <p className="font-medium text-sm">{contract.expert_id.slice(0, 8)}...</p>
          </div>
        </div>

        {/* Signature Status */}
        <div className="border-t pt-4 mt-4">
          <h3 className="font-medium mb-2">Signatures</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-3 rounded-lg ${contract.company_signed_at ? 'bg-green-50' : 'bg-gray-50'}`}>
              <p className="text-sm text-gray-500">Company</p>
              {contract.company_signed_at ? (
                <p className="text-green-700">Signed {formatDateTime(contract.company_signed_at)}</p>
              ) : (
                <p className="text-gray-500">Not signed</p>
              )}
            </div>
            <div className={`p-3 rounded-lg ${contract.expert_signed_at ? 'bg-green-50' : 'bg-gray-50'}`}>
              <p className="text-sm text-gray-500">Expert</p>
              {contract.expert_signed_at ? (
                <p className="text-green-700">Signed {formatDateTime(contract.expert_signed_at)}</p>
              ) : (
                <p className="text-gray-500">Not signed</p>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          {canSendForSigning && (
            <button
              onClick={handleSendForSigning}
              disabled={processing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Send for Signing
            </button>
          )}
          {canSign && (
            <button
              onClick={handleSignContract}
              disabled={processing}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Sign Contract
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => setShowCancelModal(true)}
              disabled={processing}
              className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50"
            >
              Cancel Contract
            </button>
          )}
          {contract.status === 'active' && (
            <button
              onClick={() => setShowDisputeModal(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Open Dispute
            </button>
          )}
        </div>
      </div>

      {/* Milestones */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Milestones</h2>

        {milestones.length === 0 ? (
          <p className="text-gray-500">No milestones defined</p>
        ) : (
          <div className="space-y-4">
            {milestones.map((milestone) => (
              <MilestoneCard
                key={milestone.id}
                milestone={milestone}
                contractStatus={contract.status}
                onFund={() => setShowFundModal(milestone)}
                onReview={(submission) => setShowReviewModal({ milestone, submission })}
                onApprovePayment={() => handleApprovePayment(milestone.id)}
                processing={processing}
              />
            ))}
          </div>
        )}
      </div>

      {/* Disputes */}
      {disputes.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Disputes</h2>
          <div className="space-y-4">
            {disputes.map((dispute) => (
              <DisputeCard
                key={dispute.id}
                dispute={dispute}
                contractId={contractId}
                onRespond={loadContractData}
              />
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Messages</h2>

        <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
          {messages.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No messages yet</p>
          ) : (
            messages.map((message) => (
              <MessageBubble key={message.id} message={message} isOwn={message.sender_type === 'company'} />
            ))
          )}
        </div>

        {contract.status === 'active' && (
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Type a message..."
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Send
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showReviewModal && (
        <ReviewSubmissionModal
          milestone={showReviewModal.milestone}
          submission={showReviewModal.submission}
          onClose={() => setShowReviewModal(null)}
          onSubmit={(data) => handleReviewSubmission(showReviewModal.milestone.id, showReviewModal.submission.id, data)}
          processing={processing}
        />
      )}

      {showFundModal && (
        <FundMilestoneModal
          milestone={showFundModal}
          onClose={() => setShowFundModal(null)}
          onSubmit={(paymentMethodId) => handleFundMilestone(showFundModal.id, paymentMethodId)}
          processing={processing}
        />
      )}

      {showDisputeModal && (
        <CreateDisputeModal
          milestones={milestones}
          onClose={() => setShowDisputeModal(false)}
          onSubmit={handleCreateDispute}
          processing={processing}
        />
      )}

      {showCancelModal && (
        <CancelContractModal
          onClose={() => setShowCancelModal(false)}
          onSubmit={handleCancelContract}
          processing={processing}
        />
      )}
    </div>
  );
}

// ============================================================================
// Milestone Card
// ============================================================================

interface MilestoneCardProps {
  milestone: Milestone;
  contractStatus: string;
  onFund: () => void;
  onReview: (submission: MilestoneSubmission) => void;
  onApprovePayment: () => void;
  processing: boolean;
}

function MilestoneCard({ milestone, contractStatus, onFund, onReview, onApprovePayment, processing }: MilestoneCardProps) {
  const statusColor = getMilestoneStatusColor(milestone.status);
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    orange: 'bg-orange-100 text-orange-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
  };

  const latestSubmission = milestone.submissions?.length
    ? milestone.submissions[milestone.submissions.length - 1]
    : null;

  const canFund = contractStatus === 'active' && milestone.status === 'pending' && !milestone.escrow_id;
  const canReview = milestone.status === 'submitted' && latestSubmission?.status === 'pending_review';
  const canApprovePayment = milestone.status === 'approved' && milestone.escrow_id;

  return (
    <div className="border rounded-lg p-4">
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">#{milestone.sequence_number}</span>
            <h3 className="font-medium">{milestone.title}</h3>
          </div>
          {milestone.description && (
            <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
          )}
        </div>
        <div className="text-right">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClasses[statusColor] || colorClasses.gray}`}>
            {getMilestoneStatusLabel(milestone.status)}
          </span>
          <p className="text-lg font-semibold mt-1">
            {formatContractAmount(milestone.amount_cents, 'EUR')}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
        {milestone.due_date && (
          <span>Due: {formatDate(milestone.due_date)}</span>
        )}
        <span>Revisions: {milestone.revision_count}/{milestone.max_revisions}</span>
        {milestone.escrow_id && (
          <span className="text-green-600">Escrow funded</span>
        )}
      </div>

      {/* Latest Submission Info */}
      {latestSubmission && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium">Latest Submission (Rev {latestSubmission.revision_number})</p>
          {latestSubmission.submission_notes && (
            <p className="text-sm text-gray-600 mt-1">{latestSubmission.submission_notes}</p>
          )}
          {latestSubmission.deliverable_urls && latestSubmission.deliverable_urls.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-gray-500">Deliverables:</p>
              {latestSubmission.deliverable_urls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block">
                  {url}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 mt-3">
        {canFund && (
          <button
            onClick={onFund}
            disabled={processing}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Fund Escrow
          </button>
        )}
        {canReview && latestSubmission && (
          <button
            onClick={() => onReview(latestSubmission)}
            disabled={processing}
            className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
          >
            Review Submission
          </button>
        )}
        {canApprovePayment && (
          <button
            onClick={onApprovePayment}
            disabled={processing}
            className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            Release Payment
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Dispute Card
// ============================================================================

interface DisputeCardProps {
  dispute: Dispute;
  contractId: string;
  onRespond: () => void;
}

function DisputeCard({ dispute, contractId, onRespond }: DisputeCardProps) {
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  async function handleRespond() {
    try {
      setProcessing(true);
      await respondToDispute(contractId, dispute.id, { message: responseMessage });
      setResponseMessage('');
      setShowResponseForm(false);
      onRespond();
    } catch (e) {
      console.error('Failed to respond to dispute:', e);
    } finally {
      setProcessing(false);
    }
  }

  const isOpen = ['open', 'under_review'].includes(dispute.status);

  return (
    <div className="border rounded-lg p-4">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="font-medium">{dispute.title}</h3>
          <p className="text-sm text-gray-500">{dispute.dispute_type}</p>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          isOpen ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {getDisputeStatusLabel(dispute.status)}
        </span>
      </div>

      <p className="text-sm text-gray-600">{dispute.description}</p>

      {dispute.responses && dispute.responses.length > 0 && (
        <div className="mt-3 space-y-2">
          {dispute.responses.map((response) => (
            <div key={response.id} className="p-2 bg-gray-50 rounded text-sm">
              <span className="font-medium capitalize">{response.responder_type}: </span>
              {response.message}
            </div>
          ))}
        </div>
      )}

      {isOpen && (
        <div className="mt-3">
          {showResponseForm ? (
            <div className="space-y-2">
              <textarea
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder="Your response..."
                className="w-full p-2 border border-gray-300 rounded text-sm"
                rows={3}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleRespond}
                  disabled={!responseMessage.trim() || processing}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {processing ? 'Sending...' : 'Send Response'}
                </button>
                <button
                  onClick={() => setShowResponseForm(false)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowResponseForm(true)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Add Response
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Message Bubble
// ============================================================================

interface MessageBubbleProps {
  message: ContractMessage;
  isOwn: boolean;
}

function MessageBubble({ message, isOwn }: MessageBubbleProps) {
  if (message.is_system_message) {
    return (
      <div className="text-center text-sm text-gray-500 py-2">
        {message.message}
      </div>
    );
  }

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] p-3 rounded-lg ${
        isOwn ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'
      }`}>
        <p className="text-sm">{message.message}</p>
        <p className={`text-xs mt-1 ${isOwn ? 'text-blue-200' : 'text-gray-500'}`}>
          {formatDateTime(message.created_at)}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Modals
// ============================================================================

interface CreateContractModalProps {
  onClose: () => void;
  onSubmit: (data: CreateContractRequest) => void;
}

function CreateContractModal({ onClose, onSubmit }: CreateContractModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [expertId, setExpertId] = useState('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [milestones, setMilestones] = useState<CreateMilestoneRequest[]>([
    { title: '', amount_cents: 0 },
  ]);
  const [processing, setProcessing] = useState(false);

  function addMilestone() {
    setMilestones([...milestones, { title: '', amount_cents: 0 }]);
  }

  function updateMilestone(index: number, field: keyof CreateMilestoneRequest, value: string | number) {
    const updated = [...milestones];
    updated[index] = { ...updated[index], [field]: value };
    setMilestones(updated);
  }

  function removeMilestone(index: number) {
    if (milestones.length > 1) {
      setMilestones(milestones.filter((_, i) => i !== index));
    }
  }

  const totalAmount = milestones.reduce((sum, m) => sum + (m.amount_cents || 0), 0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setProcessing(true);

    onSubmit({
      expert_id: expertId,
      title,
      description: description || undefined,
      scope_of_work: scopeOfWork,
      total_amount_cents: totalAmount,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      milestones: milestones.filter((m) => m.title && m.amount_cents > 0),
    });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 my-8">
        <h2 className="text-xl font-bold mb-6">Create Contract</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expert ID *</label>
            <input
              type="text"
              value={expertId}
              onChange={(e) => setExpertId(e.target.value)}
              required
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Expert user ID"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Contract title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scope of Work *</label>
            <textarea
              value={scopeOfWork}
              onChange={(e) => setScopeOfWork(e.target.value)}
              required
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Detailed scope of work..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Milestones *</label>
              <button
                type="button"
                onClick={addMilestone}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                + Add Milestone
              </button>
            </div>

            <div className="space-y-3">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={milestone.title}
                      onChange={(e) => updateMilestone(index, 'title', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Milestone title"
                    />
                  </div>
                  <div className="w-32">
                    <input
                      type="number"
                      value={milestone.amount_cents / 100 || ''}
                      onChange={(e) => updateMilestone(index, 'amount_cents', parseFloat(e.target.value) * 100 || 0)}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Amount"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {milestones.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMilestone(index)}
                      className="p-2 text-red-600 hover:text-red-700"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="text-right mt-2">
              <span className="text-sm text-gray-500">Total: </span>
              <span className="font-semibold">{formatContractAmount(totalAmount, 'EUR')}</span>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={processing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={processing || !title || !expertId || !scopeOfWork || milestones.every((m) => !m.title || !m.amount_cents)}
            >
              {processing ? 'Creating...' : 'Create Contract'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface ReviewSubmissionModalProps {
  milestone: Milestone;
  submission: MilestoneSubmission;
  onClose: () => void;
  onSubmit: (data: ReviewSubmissionRequest) => void;
  processing: boolean;
}

function ReviewSubmissionModal({ milestone, submission, onClose, onSubmit, processing }: ReviewSubmissionModalProps) {
  const [decision, setDecision] = useState<SubmissionStatus>('approved');
  const [feedback, setFeedback] = useState('');
  const [qualityRating, setQualityRating] = useState(5);
  const [revisionItems, setRevisionItems] = useState<string[]>(['']);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      decision,
      feedback: feedback || undefined,
      quality_rating: decision === 'approved' ? qualityRating : undefined,
      revision_items: decision === 'revision_requested'
        ? revisionItems.filter((item) => item.trim()).map((description) => ({ description }))
        : undefined,
    });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4">
        <h2 className="text-xl font-bold mb-2">Review Submission</h2>
        <p className="text-gray-600 mb-6">{milestone.title} - Revision {submission.revision_number}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Decision *</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={decision === 'approved'}
                  onChange={() => setDecision('approved')}
                  className="mr-2"
                />
                Approve
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={decision === 'revision_requested'}
                  onChange={() => setDecision('revision_requested')}
                  className="mr-2"
                />
                Request Revision
              </label>
            </div>
          </div>

          {decision === 'approved' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quality Rating</label>
              <input
                type="range"
                min="1"
                max="5"
                value={qualityRating}
                onChange={(e) => setQualityRating(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-500">
                <span>Poor</span>
                <span>Excellent ({qualityRating}/5)</span>
              </div>
            </div>
          )}

          {decision === 'revision_requested' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Revision Items</label>
              {revisionItems.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => {
                      const updated = [...revisionItems];
                      updated[index] = e.target.value;
                      setRevisionItems(updated);
                    }}
                    className="flex-1 p-2 border border-gray-300 rounded"
                    placeholder="What needs to be changed..."
                  />
                  {revisionItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setRevisionItems(revisionItems.filter((_, i) => i !== index))}
                      className="text-red-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setRevisionItems([...revisionItems, ''])}
                className="text-sm text-blue-600"
              >
                + Add item
              </button>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Feedback</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="Additional feedback for the expert..."
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={processing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                decision === 'approved'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-yellow-600 hover:bg-yellow-700'
              }`}
              disabled={processing}
            >
              {processing ? 'Submitting...' : decision === 'approved' ? 'Approve' : 'Request Revision'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface FundMilestoneModalProps {
  milestone: Milestone;
  onClose: () => void;
  onSubmit: (paymentMethodId: string) => void;
  processing: boolean;
}

function FundMilestoneModal({ milestone, onClose, onSubmit, processing }: FundMilestoneModalProps) {
  const [paymentMethodId, setPaymentMethodId] = useState('');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-2">Fund Milestone Escrow</h2>
        <p className="text-gray-600 mb-6">{milestone.title}</p>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Milestone Amount</span>
            <span className="font-semibold">{formatContractAmount(milestone.amount_cents, 'EUR')}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Platform Fee ({(milestone.platform_fee_cents / milestone.amount_cents * 100).toFixed(0)}%)</span>
            <span className="text-gray-500">{formatContractAmount(milestone.platform_fee_cents, 'EUR')}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Expert Receives</span>
            <span className="text-gray-500">{formatContractAmount(milestone.expert_amount_cents, 'EUR')}</span>
          </div>
        </div>

        <div className="mb-6 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
          <p>
            In production, your saved payment methods would appear here.
            For testing, enter a Stripe payment method ID.
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method ID</label>
          <input
            type="text"
            value={paymentMethodId}
            onChange={(e) => setPaymentMethodId(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg"
            placeholder="pm_..."
          />
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={processing}
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(paymentMethodId)}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            disabled={processing || !paymentMethodId}
          >
            {processing ? 'Processing...' : 'Fund Escrow'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface CreateDisputeModalProps {
  milestones: Milestone[];
  onClose: () => void;
  onSubmit: (data: { milestone_id?: string; dispute_type: DisputeType; title: string; description: string }) => void;
  processing: boolean;
}

function CreateDisputeModal({ milestones, onClose, onSubmit, processing }: CreateDisputeModalProps) {
  const [milestoneId, setMilestoneId] = useState('');
  const [disputeType, setDisputeType] = useState<DisputeType>('quality');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      milestone_id: milestoneId || undefined,
      dispute_type: disputeType,
      title,
      description,
    });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4">
        <h2 className="text-xl font-bold mb-6">Open Dispute</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Related Milestone (Optional)</label>
            <select
              value={milestoneId}
              onChange={(e) => setMilestoneId(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg"
            >
              <option value="">General Contract Dispute</option>
              {milestones.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dispute Type *</label>
            <select
              value={disputeType}
              onChange={(e) => setDisputeType(e.target.value as DisputeType)}
              required
              className="w-full p-3 border border-gray-300 rounded-lg"
            >
              <option value="quality">Quality Issue</option>
              <option value="scope">Scope Dispute</option>
              <option value="deadline">Deadline Issue</option>
              <option value="payment">Payment Dispute</option>
              <option value="communication">Communication Issue</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="Brief summary of the issue"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="Detailed description of the dispute..."
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={processing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              disabled={processing || !title || !description}
            >
              {processing ? 'Creating...' : 'Open Dispute'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface CancelContractModalProps {
  onClose: () => void;
  onSubmit: (reason: string) => void;
  processing: boolean;
}

function CancelContractModal({ onClose, onSubmit, processing }: CancelContractModalProps) {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-2">Cancel Contract</h2>
        <p className="text-gray-600 mb-6">This action cannot be undone.</p>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason for cancellation *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg"
            placeholder="Why is this contract being cancelled?"
          />
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={processing}
          >
            Keep Contract
          </button>
          <button
            onClick={() => onSubmit(reason)}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            disabled={processing || !reason.trim()}
          >
            {processing ? 'Cancelling...' : 'Cancel Contract'}
          </button>
        </div>
      </div>
    </div>
  );
}
